import { TypedDocumentNode, gql } from '@apollo/client';
import { CSSProperties } from 'react';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_ENTER_SEASONALITY_COMPETITION } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FeaturedBanner_so5LeaderboardGroupInterface } from './__generated__/index.graphql';

const Root = styled(ButtonBase)`
  width: 100%;
  max-width: 345px;
  height: 200px;
  margin: auto;
  position: relative;
  display: block;

  background-image: var(--bg);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);

  @media ${tabletAndAbove} {
    max-width: 1825px;
    border-radius: unset;
    border: unset;
  }
`;
const Header = styled(SBHorizontal)`
  position: absolute;
  inset: var(--double-unit) var(--double-unit) auto;

  @media ${tabletAndAbove} {
    inset: var(--double-unit) var(--triple-unit) auto;
  }
`;
const Summary = styled(BodyS)`
  padding: 2px var(--unit);
  border-radius: var(--intermediate-unit);
  backdrop-filter: blur(4px);
  background-color: var(--c-nd-100);
`;

type Props = {
  so5LeaderboardGroupInterface: FeaturedBanner_so5LeaderboardGroupInterface;
};

export const FeaturedBanner = ({ so5LeaderboardGroupInterface }: Props) => {
  const track = useEvents();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { generateSportContextPath } = useSportContext();
  const seasonality = so5LeaderboardGroupInterface.seasonalities.includes(
    So5LeaderboardSeasonality.IN_SEASON
  )
    ? So5LeaderboardSeasonality.IN_SEASON
    : So5LeaderboardSeasonality.ALL_SEASONS;

  const pictureUrl = so5LeaderboardGroupInterface.seasonalities.length
    ? {
        [So5LeaderboardSeasonality.IN_SEASON]:
          so5LeaderboardGroupInterface.inSeasonPictureUrl,
        [So5LeaderboardSeasonality.ALL_SEASONS]:
          so5LeaderboardGroupInterface.classicPictureUrl,
      }[seasonality]
    : so5LeaderboardGroupInterface.pictureUrl;

  return (
    <Root
      style={
        {
          '--bg': `url(${isTabletAndAbove ? so5LeaderboardGroupInterface.bannerUrl : pictureUrl})`,
        } as CSSProperties
      }
      to={generateSportContextPath(
        ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_ENTER_SEASONALITY_COMPETITION,
        {
          params: {
            eventType:
              so5LeaderboardGroupInterface.so5Fixture.type.toLowerCase(),
            competition: so5LeaderboardGroupInterface.slug,
            fixture: so5LeaderboardGroupInterface.so5Fixture.slug,
            seasonality: seasonality.toLowerCase(),
          },
        }
      )}
      onClick={() =>
        track('Click on lobby featured banner', {
          so5LeaderboardGroupSlug: so5LeaderboardGroupInterface.slug,
        })
      }
    >
      <Header>
        <Summary as="p">{so5LeaderboardGroupInterface.leagueSummary}</Summary>
      </Header>
    </Root>
  );
};

FeaturedBanner.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment FeaturedBanner_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      headerUrl
      bannerUrl
      pictureUrl
      displayName
      leagueSummary
      seasonalities
      inSeasonPictureUrl: pictureUrl(seasonality: IN_SEASON)
      classicPictureUrl: pictureUrl(seasonality: ALL_SEASONS)
      so5Fixture {
        slug
        type
      }
    }
  ` as TypedDocumentNode<FeaturedBanner_so5LeaderboardGroupInterface>,
};
